import { SkuItem } from 'common/modules/inventory';
import errorHandler from 'common/services/errorHandler';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import { getTypedKeys } from 'common/utils/objects';
import { TFunction } from 'i18next';
import { isEmpty } from 'lodash';
import { skuItemToRow } from 'views/Inventory/SkuView/SkuTable/rows';
import { getNewSkuItem } from 'views/Inventory/utils';
import { AllowedImportField, DataRowObject, getValidValuesText } from '../utils';

export type SkuImportUpdateObject = Pick<SkuItem, 'id'> &
	Partial<Pick<SkuItem, 'skuName' | 'skuCode' | 'itemType'>>;
export type CsvSkuFields = Extract<AllowedImportField, 'sku_code' | 'sku_name' | 'type'>;
export type CsvSkuData = { [key in CsvSkuFields]?: string };

export type SkuImport = {
	toAdd: {
		data: SkuItem;
		csvFields: {
			data: CsvSkuData;
		};
	}[];
	toUpdate: {
		data: SkuImportUpdateObject;
		csvFields: {
			data: CsvSkuData;
			updatedFields: CsvSkuFields[];
		};
	}[];
	invalid: {
		csvFields: {
			data: CsvSkuData;
			invalidFields: CsvSkuData;
		};
	}[];
};

const validateSkuFields = (
	sku: CsvSkuData,
	opts: { existingSku: SkuItem | undefined },
	t: TFunction,
) => {
	const { existingSku } = opts;
	let errors: CsvSkuData = {};
	const { sku_code, sku_name, type } = sku;
	const importItemType: SkuItem['itemType'] | undefined =
		type === 'bulk' ? 'bulk' : type === 'single' ? 'single' : undefined;
	const emptySkuCode = sku_code === '';
	const fieldCannotBeEmptyString = t('validation.fieldCannotBeEmpty', 'This field cannot be empty');
	const incorrectValueString = t('validation.incorrectValue', 'Incorrect value');
	if (emptySkuCode) {
		errors.sku_code = fieldCannotBeEmptyString;
	}
	const emptySkuName = sku_name === '';
	if (emptySkuName) {
		errors.sku_name = fieldCannotBeEmptyString;
	}
	const newSkuNoSkuCode = !existingSku && sku_code == null;
	if (newSkuNoSkuCode) {
		errors.sku_code = fieldCannotBeEmptyString;
	}
	const newSkuNoSkuName = !existingSku && sku_name == null;
	if (newSkuNoSkuName) {
		errors.sku_name = fieldCannotBeEmptyString;
	}
	const invalidTypeChange =
		!!existingSku && existingSku.quantity !== 0 && existingSku.itemType !== importItemType;
	const incorrectItemType = !!type && !importItemType;
	const validTypeValuesText = getValidValuesText(t, 'type');
	if (!existingSku && !type) {
		errors.type = `${fieldCannotBeEmptyString}. ${validTypeValuesText}`;
	}
	if (invalidTypeChange) {
		errors.type = `${incorrectValueString} ${type}. ${t(
			'inventory.import.skuTypeCannotBeChanged',
			'Type of SKU can only be changed if SKU has no articles',
		)}.`;
	}
	if (incorrectItemType) {
		errors.type = `${incorrectValueString}. ${validTypeValuesText}`;
	}
	if (!isEmpty(errors)) {
		return { errors, data: null };
	}
	return {
		errors: null,
		data: {
			skuName: sku_name,
			skuCode: sku_code,
			itemType: importItemType,
		},
	};
};

const getImportedSkuRowFieldDiff = (sku: CsvSkuData, existingSku: SkuItem) => {
	const existingSkuAsDataRow = skuItemToRow(existingSku, []);
	const skuDiff = getTypedKeys(sku).filter((key) => {
		const value = sku[key];
		const existingValue = existingSkuAsDataRow[key];
		const existingFieldNotFoundAndNewValueEmpty =
			existingValue == null && (value == null || value === '');
		if (value == null || existingFieldNotFoundAndNewValueEmpty) return false;
		return existingValue !== value;
	});
	return skuDiff;
};

export const getSkuImportData = (
	rows: DataRowObject[],
	skuItems: SkuItem[],
	shopId: string,
	t: TFunction,
): SkuImport => {
	const allUniqueSkus = rows.reduce((tot, row) => {
		const rowSkuName = row.sku_name;
		const rowSkuCode = row.sku_code;
		const rowSkuType = row.type?.toLocaleLowerCase();
		if (!rowSkuCode) return tot;
		if (tot.find((skus) => skus.sku_code === rowSkuCode)) return tot;
		return [...tot, { sku_code: rowSkuCode, sku_name: rowSkuName, type: rowSkuType }];
	}, [] as CsvSkuData[]);
	const skuData = allUniqueSkus.reduce(
		(tot, sku) => {
			const { sku_code } = sku;
			const existingSkuItem = skuItems.find((item) => item.skuCode === sku_code);
			const updatedFields = !!existingSkuItem
				? getImportedSkuRowFieldDiff(sku, existingSkuItem)
				: undefined;
			const noFieldChanges = !!existingSkuItem && !updatedFields?.length;
			const skuAlreadyUpdated =
				!!existingSkuItem && tot.toUpdate.some((sku) => sku.data.id === existingSkuItem.id);
			if (noFieldChanges || skuAlreadyUpdated) return tot;
			const skuFields = validateSkuFields(sku, { existingSku: existingSkuItem }, t);
			if (!!skuFields.errors) {
				const invalidSku: SkuImport['invalid'][number] = {
					csvFields: {
						data: sku,
						invalidFields: skuFields.errors,
					},
				};
				return {
					...tot,
					invalid: [...tot.invalid, invalidSku],
				};
			}
			const { skuCode, skuName, itemType } = skuFields.data;
			if (!!existingSkuItem) {
				const updatedSku: SkuImport['toUpdate'][number] = {
					data: { id: existingSkuItem.id, skuName, itemType },
					csvFields: {
						data: sku,
						updatedFields: updatedFields ?? [],
					},
				};
				return {
					...tot,
					toUpdate: [...tot.toUpdate, updatedSku],
				};
			}
			if (!skuCode || !skuName || !itemType) {
				errorHandler.report('Invalid SKU format', {
					skuCode,
					skuName,
					itemType,
				});
				const invalidSku: SkuImport['invalid'][number] = {
					csvFields: {
						data: sku,
						invalidFields: {
							sku_code:
								t('inventory.import.invalidSkuFormatError', 'Unknown error: Invalid SKU format') ??
								'',
						},
					},
				};
				return {
					...tot,
					invalid: [...tot.invalid, invalidSku],
				};
			}
			const newSkuItem = getNewSkuItem({
				id: newFirestoreId(),
				name: skuName,
				skuCode: skuCode,
				shopId,
				itemType,
			});
			const toAddSku: SkuImport['toAdd'][number] = {
				data: newSkuItem,
				csvFields: {
					data: sku,
				},
			};
			return {
				...tot,
				toAdd: [...tot.toAdd, toAddSku],
			};
		},
		{ toUpdate: [], toAdd: [], invalid: [] } as SkuImport,
	);
	return skuData;
};
